import type { FlowAPI } from '@wix/yoshi-flow-editor';

let panoramaClient: FlowAPI['panoramaClient'];
let panoramaErrorMonitor:
  | ReturnType<NonNullable<FlowAPI['panoramaClient']>['errorMonitor']>
  | undefined;

type Options = {
  metaSiteId: string;
  instanceId: string;
  panoramaClient: FlowAPI['panoramaClient'];
};

export const initMonitoring = (options: Options) => {
  if (options.panoramaClient) {
    panoramaClient = options.panoramaClient;
    panoramaErrorMonitor = options.panoramaClient.errorMonitor({
      data: { tags: { metaSiteId: options.metaSiteId, instanceId: options.instanceId } },
    });
  }
};

export const reportError = (error: unknown) => {
  if (typeof error === 'string') {
    return panoramaErrorMonitor?.reportError(new Error(error));
  } else if (error instanceof Error) {
    return panoramaErrorMonitor?.reportError(error);
  }
};

export function toMonitored<T>(
  interactionName: string,
  promise: Promise<T>,
  rethrow?: true,
  timeout?: number,
): Promise<T>;
export function toMonitored<T>(
  interactionName: string,
  promise: Promise<T>,
  rethrow: false,
  timeout?: number,
): Promise<T | undefined>;
export async function toMonitored<T>(
  interactionName: string,
  promise: Promise<T>,
  rethrow: boolean = true,
  timeout?: number,
): Promise<T | undefined> {
  let isResolved = false;

  try {
    interactionStarted(interactionName);

    if (timeout) {
      setTimeout(() => {
        interactionFailed(interactionName, new Error(`Timeout exceeded: "${interactionName}"`));
        isResolved = true;
      }, timeout);
    }

    const response = await promise;

    if (!isResolved) {
      interactionEnded(interactionName);
    }

    return response;
  } catch (err) {
    if (err instanceof Error) {
      interactionFailed(interactionName, err);
    } else {
      interactionFailed(interactionName, new Error(String(err)));
    }
    if (rethrow) {
      throw err;
    }
  }
}

export const log = (message: string, data?: Record<string, any>) => {
  panoramaClient?.logger().info(message, data);
};

const interactionStarted = (interactionName: string) => {
  try {
    return panoramaClient?.transaction(interactionName).start();
  } catch (err) {
    if (err instanceof Error) {
      panoramaErrorMonitor?.reportError(err, {
        tags: { interactionName, fedopsError: 'interactionStarted' },
      });
    }
  }
};

const interactionEnded = (interactionName: string) => {
  try {
    return panoramaClient?.transaction(interactionName).finish();
  } catch (err) {
    if (err instanceof Error) {
      panoramaErrorMonitor?.reportError(err, {
        tags: { interactionName, fedopsError: 'interactionEnded' },
      });
    }
  }
};

const interactionFailed = (interactionName: string, err: Error, message?: string) => {
  panoramaClient?.logger().error(`${interactionName}${message ? ': ' + message : ''}`, err);

  panoramaErrorMonitor?.reportError(err, {
    tags: { interactionName, fedopsError: 'interactionFailed' },
  });
};
